import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import Input from './Input';

interface Props {
  value?: number;
  name: string;
  id?: string;
  placeholder?: string;
  labelText?: string;
  helperText?: string;
  errorText?: string;
  onChange: (value: number) => void;
  minCents?: number;
  maxCents?: number;
  disabled?: boolean;
  required?: boolean;
  currency?: string;
  className?: string;
}

function currencyToCents(input: string): number | null {
  const cleanedInput = input.replace(/[^0-9.]/g, '');
  const parts = cleanedInput.split('.');

  if (parts.length > 2) {
    return null;
  }

  const hasDecimal = parts.length === 2;
  const integerPart = Number.parseInt(parts[0], 10) || 0;

  const decimalPart = hasDecimal ? Number.parseInt(parts[1].slice(0, 2).padEnd(2, '0'), 10) : 0;

  if (!Number.isNaN(integerPart) && (!hasDecimal || !Number.isNaN(decimalPart))) {
    return integerPart * 100 + decimalPart;
  }

  return null;
}

const CurrencyInput = ({
  value,
  onChange,
  name,
  placeholder,
  labelText,
  helperText,
  errorText,
  minCents,
  maxCents,
  disabled = false,
  required = false,
  className,
  currency = 'USD',
}: Props) => {
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
      }),
    [currency],
  );

  const [friendlyAmount, setFriendlyAmount] = useState(value ? formatter.format(value / 100) : undefined);
  const [amountCents, setAmountCents] = useState<number | undefined>(value);

  useEffect(() => {
    if (friendlyAmount) {
      setAmountCents(currencyToCents(friendlyAmount) || 0);
    } else {
      setAmountCents(undefined);
    }
  }, [friendlyAmount]);

  useEffect(() => {
    if (amountCents || amountCents === 0) {
      setFriendlyAmount(formatter.format(amountCents / 100));
    }
  }, [formatter]);

  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }

    if (amountCents !== undefined) {
      onChange(amountCents);
    }
  }, [amountCents, onChange]);

  return (
    <div className="flex flex-row items-center space-x-2">
      <Input
        className={className}
        type="text"
        name={name}
        disabled={disabled}
        placeholder={placeholder || formatter.format(0)}
        value={friendlyAmount}
        onChange={(e: ChangeEvent) => setFriendlyAmount((e.currentTarget as HTMLInputElement).value)}
        label={labelText}
        helperText={helperText}
        errorText={errorText}
        onBlur={() => {
          if (friendlyAmount && (amountCents || amountCents === 0)) {
            setFriendlyAmount(formatter.format(amountCents / 100));
          }
        }}
        required={required}
        onKeyDown={(e: React.KeyboardEvent) => {
          // subtract 100 cents when the user presses the down arrow
          if (e.key === 'ArrowDown' && (amountCents || amountCents === 0)) {
            const decrease = e.shiftKey ? 1000 : 100;
            const val = formatter.format((amountCents - decrease) / 100);

            // if the amount is less than the min, set it to the min
            if (minCents) {
              setFriendlyAmount(amountCents - decrease < minCents ? formatter.format(minCents / 100) : val);
            } else {
              setFriendlyAmount(val);
            }
          }

          // add 100 cents when the user presses the up arrow
          if (e.key === 'ArrowUp' && (amountCents || amountCents === 0)) {
            const increase = e.shiftKey ? 1000 : 100;
            const val = formatter.format((amountCents + increase) / 100);

            // if the amount is greater than the max, set it to the max
            if (maxCents) {
              setFriendlyAmount(amountCents + increase > maxCents ? formatter.format(maxCents / 100) : val);
            } else {
              setFriendlyAmount(val);
            }
          }

          if (e.key === 'Enter' && (amountCents || amountCents === 0)) {
            setFriendlyAmount(formatter.format(amountCents / 100));
          }
        }}
      />
    </div>
  );
};

export default CurrencyInput;
